import React, { memo } from 'react';
import { Menu } from '@mui/icons-material';
import {
  IconButton,
  Typography,
  Toolbar,
  AppBar,
  Paper,
  Box,
  useTheme,
} from '@mui/material';

import { useDrawer } from '../hooks';
import ModalFeedback from '../../pages/tenants/components/ModalFeedback';
interface PageLayoutProps {
  /** Titulo da página atual */
  title: string;
  /** Ferramentas que estarão no app bar */
  tools?: React.ReactNode;
  /** Alinha o layout flex contido no interior do componente */
  alignTools?: 'end' | 'start';
  /** Usado para adiconar uma barra extra de navegação */
  navigation?: React.ReactNode;
  /** Alinha o layout flex contido no interior do componente */
  alignNavigation?: 'end' | 'start';
  /** Conteúdo da página ex: Forms, Inputs, Labels, Grids... */
  children?: React.ReactNode;
}

/**
 * Usado para definir uma estrutura básica para todas as páginas
 * @param title Titulo da página atual
 * @param tools Ferramentas que estarão no app bar
 * @param alignTools alinhamento das ferramentas
 * @param children Conteúdo da página ex: Forms, Inputs, Labels, Grids...
 */
export const PageLayout: React.FC<PageLayoutProps> = memo(
  ({ title, alignTools, alignNavigation, navigation, tools, children }) => {
    const { toggleDrawer, drawerWidth, isDrawerMobile, isDrawerOpen } =
      useDrawer();

    const theme = useTheme();

    // Offset in pixels used when the drawer is open on non-mobile screens
    const leftOffset = !isDrawerMobile && isDrawerOpen ? drawerWidth : 0;
    const appBarWidth = `calc(100% - ${leftOffset}px)`;

    return (
      <>
        <AppBar
          color="default"
          sx={{
            transition: 'all .1s',
            display: 'flex',
            // Keep AppBar in sync with drawer: shift and shrink when open
            width: appBarWidth,
            left: leftOffset,
            paddingLeft: isDrawerOpen ? 0 : 2,
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              transition: 'all .1s',
              display: 'flex',
              height: 80,
              overflow: 'auto',
              justifyContent: 'space-between',
              pl: isDrawerOpen ? '16rem' : 5,
            }}
          >
            <Toolbar
              sx={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              {isDrawerMobile && (
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={toggleDrawer}
                  aria-label="Abre o menu lateral"
                >
                  <Menu />
                </IconButton>
              )}
              <Typography variant="h6">{title}</Typography>
              <ModalFeedback />
            </Toolbar>
          </Box>

          <Box // Barra de botões de acesso
            component={Paper}
            sx={{
              p: 1,
              height: 56,
              display: 'flex',
              pl: isDrawerOpen ? '16.875rem' : 8,
              pr: 2,
              boxShadow: 'none',
              borderRadius: '0px',
              alignItems: 'center',
              color: 'textPrimary',
              transition: 'all .1s',
              bgcolor: theme.palette.primary.main,
              // Align tools bar by provided prop, defaulting to start
              justifyContent: `flex-${alignTools ?? 'start'}`,
            }}
          >
            {tools}
          </Box>

          {/* {navigation &&
          <Box // Barra de navegação
            padding={8}
            display="flex"
            width='100vw'
            height='100vh'
            paddingLeft={1}
            paddingRight={1}
            boxShadow="none"
            borderRadius='0px'
            component={Paper}
            alignItems="center"
            bgcolor='red'
            justifyContent={`flex-${alignNavigation}`}
            sx={{ transition: 'all .1s' }}
            marginLeft={
              isDrawerMobile
                ? 0
                : theme.spacing(isDrawerOpen ? drawerWidth : 2)}
          >{navigation}</Box>
        } */}
        </AppBar>

        <Box
          sx={{
            p: 2,
            pt: 20,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            transition: 'all .1s',
            // Content area respects drawer offset and adds spacing when closed
            ml: isDrawerMobile
              ? 0
              : isDrawerOpen
                ? `${leftOffset}px`
                : `calc(${leftOffset}px + ${theme.spacing(5)})`,
          }}
        >
          <Paper
            variant="outlined"
            component={Box}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              justifyContent: 'center',
            }}
          >
            {children}
          </Paper>
        </Box>
      </>
    );
  },
);
